import React, { useEffect, useRef, useState } from "react";
import { createGame, setAim, setPower, shoot, tick } from "./engine.js";
import type { PoolGame } from "./types.js";
import { TABLE_W, TABLE_H, RAIL, BALL_R, POCKETS, POCKET_R } from "./types.js";
import ConfettiSplash from "../../lib/ConfettiSplash.js";

const BALL_COLORS: Record<number, string> = {
  0: "#fff", 1: "#f1c40f", 2: "#3498db", 3: "#e74c3c",
  4: "#8e44ad", 5: "#e67e22", 6: "#2ecc71", 7: "#8b4513",
  8: "#111", 9: "#f1c40f", 10: "#3498db", 11: "#e74c3c",
  12: "#8e44ad", 13: "#e67e22", 14: "#2ecc71", 15: "#8b4513",
};

export default function PoolPage({ hideTitle = false }: { hideTitle?: boolean }) {
  const [game, setGame] = useState<PoolGame>(() => createGame());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef(game);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(0);
  const dragging = useRef(false);

  gameRef.current = game;

  function resetGame() {
    setGame(createGame());
  }

  function render(ctx: CanvasRenderingContext2D, g: PoolGame) {
    ctx.clearRect(0, 0, TABLE_W, TABLE_H);

    ctx.fillStyle = "#1a4a1a";
    ctx.fillRect(0, 0, TABLE_W, TABLE_H);

    ctx.fillStyle = "#2a6a2a";
    ctx.fillRect(RAIL - 4, RAIL - 4, TABLE_W - RAIL * 2 + 8, TABLE_H - RAIL * 2 + 8);

    ctx.fillStyle = "#1a4a1a";
    ctx.fillRect(RAIL, RAIL, TABLE_W - RAIL * 2, TABLE_H - RAIL * 2);

    for (const p of POCKETS) {
      ctx.fillStyle = "#0a2a0a";
      ctx.beginPath();
      ctx.arc(p.x, p.y, POCKET_R, 0, Math.PI * 2);
      ctx.fill();
    }

    for (const b of g.balls) {
      if (b.pocketed) continue;
      drawBall(ctx, b.x, b.y, BALL_R, b.number);
    }

    if (!g.cueBall.pocketed) {
      drawBall(ctx, g.cueBall.x, g.cueBall.y, BALL_R, 0);
    }

    if (!g.inMotion && g.winner === null) {
      const angleRad = (g.aimAngle * Math.PI) / 180;
      const lineLen = 120;
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(g.cueBall.x, g.cueBall.y);
      ctx.lineTo(g.cueBall.x + Math.cos(angleRad) * lineLen, g.cueBall.y + Math.sin(angleRad) * lineLen);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.fillStyle = "#fff";
    ctx.font = "14px monospace";
    ctx.fillText(`Player ${g.turn}`, 10, 20);
    if (g.foul) {
      ctx.fillStyle = "#e74c3c";
      ctx.fillText("Foul!", TABLE_W / 2 - 30, 20);
    }
  }

  function drawBall(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, num: number) {
    const color = BALL_COLORS[num] || "#fff";
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    if (num > 0) {
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = num === 8 ? "#fff" : "#fff";
      ctx.beginPath();
      ctx.arc(x, y, r * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#000";
      ctx.font = `${r * 0.7}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(num), x, y + 1);
    }
  }

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    render(ctx, game);
  }, [game]);

  useEffect(() => {
    if (!game.inMotion) return;
    lastTimeRef.current = performance.now();
    function loop(time: number) {
      const dt = time - lastTimeRef.current;
      lastTimeRef.current = time;
      setGame((prev) => tick(prev, dt));
      if (gameRef.current.inMotion) {
        rafRef.current = requestAnimationFrame(loop);
      }
    }
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [game.inMotion]);

  function handleCanvasDown(e: React.MouseEvent<HTMLCanvasElement>) {
    if (game.inMotion || game.winner !== null) return;
    dragging.current = true;
    updateAim(e);
  }

  function handleCanvasMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!dragging.current) return;
    updateAim(e);
  }

  function updateAim(e: React.MouseEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = (e.clientX - rect.left) * (TABLE_W / rect.width);
    const my = (e.clientY - rect.top) * (TABLE_H / rect.height);
    const dx = mx - game.cueBall.x;
    const dy = my - game.cueBall.y;
    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    const dist = Math.sqrt(dx * dx + dy * dy);
    const power = Math.min(100, dist * 0.4);
    setGame((prev) => setPower(setAim(prev, angle), power));
  }

  function handleCanvasUp() {
    if (!dragging.current) return;
    dragging.current = false;
    setGame((prev) => shoot(prev));
  }

  return (
    <div className="game-shell">
      <ConfettiSplash active={game.winner !== null} winnerName={game.winner ? `Player ${game.winner}` : undefined} />
      {!hideTitle && <h1 className="title">Pool</h1>}

      <section className="control-panel">
        <p className="status">{game.message}</p>
        <div className="actions">
          <button onClick={resetGame}>New Game</button>
        </div>
      </section>

      <section className="board-panel">
        <canvas
          ref={canvasRef}
          width={TABLE_W}
          height={TABLE_H}
          className="pool-canvas"
          onMouseDown={handleCanvasDown}
          onMouseMove={handleCanvasMove}
          onMouseUp={handleCanvasUp}
          onMouseLeave={handleCanvasUp}
        />
        <div className="how-to-play">
          <p className="how-title">How to Play</p>
          <p>1. Click and drag from the cue ball to aim and set power.</p>
          <p>2. Release to shoot. Pocket the 8-ball last to win.</p>
          <p>3. Pocketing the cue ball is a foul — turn passes.</p>
        </div>
      </section>
    </div>
  );
}
